import { signal } from "@preact/signals";
import { useEffect, useRef, useState } from "preact/hooks";
import { appendNote } from "../../data/store.ts";
import { IconClose, IconPlus } from "../Icons.tsx";
import { expireUnlessHeld } from "../lib.ts";
import { failed, refocus, route, showCard, store, toast, topTags } from "../state.ts";

// Dono: gap-capture (mobile-capture). Folha de /capturado (spec §5.3 F-M2, §10.4): depois de um compartilhamento do
// Android, primeiro só a confirmação ("Guardado."), que sai sozinha; "Adicionar detalhes" abre etiquetas e anotação para
// os cards que acabaram de nascer. Tudo pela fila de saída: vale offline. Não é modal: a grade continua por baixo (e
// utilizável); Esc, × ou "Agora não" fecham sem mexer em nada.

/** Quanto a confirmação fica na tela sem ninguém mexer nela (a barra de tempo da folha dura o mesmo, capture.css). */
export const CONFIRM_MS = 6000;

/** Cards do último compartilhamento (consumeShares em inbox.ts). */
export const captured = signal<string[]>([]);

/** Abre a folha para estes cards, em /capturado (sem empilhar histórico: Voltar não reabre a folha). */
export function showCaptured(ids: string[]) {
  captured.value = ids;
  history.replaceState(null, "", "/capturado");
  route.value = { path: "/capturado", cardId: null };
}

function closeCaptured() {
  captured.value = [];
  if (location.pathname === "/capturado") history.replaceState(null, "", "/");
  route.value = { path: "/", cardId: null };
  refocus(() => document.getElementById("main"), { preventScroll: true });
}

/** "viagem, #ideias ,  " → ["viagem", "ideias"]. */
export const tagNames = (s: string) => [...new Set(s.split(",").map((t) => t.trim().replace(/^#+/, "").trim()).filter(Boolean))];

export function CapturedSheet() {
  const path = route.value.path;
  const ids = captured.value.filter((id) => store.get(id) && !store.get(id)!.deletedAt);
  // Recarregou em /capturado (a lista não sobrevive): volta para Tudo.
  useEffect(() => void (path === "/capturado" && !captured.peek().length && closeCaptured()), [path]);
  return path === "/capturado" && ids.length ? <Sheet ids={ids} /> : null;
}

function Sheet({ ids }: { ids: string[] }) {
  const box = useRef<HTMLElement>(null);
  const [details, setDetails] = useState(false);
  const [tags, setTags] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  // Foco na folha (não num campo: no celular o teclado cobriria a tela antes de a pessoa decidir).
  useEffect(() => box.current?.focus({ preventScroll: true }), []);
  // Pediu detalhes: o foco vai para as etiquetas (o teclado abre e a folha sobe junto, capture.css).
  useEffect(() => void (details && document.getElementById("captured-tags")?.focus()), [details]);
  // Sem detalhes, a confirmação sai sozinha, salvo com o dedo, o mouse ou o foco de teclado num botão dela.
  useEffect(() => {
    if (details) return;
    let on = true;
    const held = () => !!box.current && (box.current.matches(":hover") || !!box.current.querySelector("button:focus-visible"));
    expireUnlessHeld(held, () => on && closeCaptured(), CONFIRM_MS);
    return () => void (on = false);
  }, [details]);
  const n = ids.length;
  const offline = !store.status.value.online || !navigator.onLine;

  const save = async (e: Event) => {
    e.preventDefault();
    const names = tagNames(tags);
    const text = note.trim();
    if (!names.length && !text) return closeCaptured();
    setBusy(true);
    try {
      for (const name of names) await store.applyMany(ids, { kind: "tag", name });
      for (const id of text ? ids : []) {
        const c = store.get(id);
        if (c) await store.update(id, appendNote(c.note, c.noteText, text));
      }
    } catch (err) {
      setBusy(false);
      return failed(err);
    }
    toast({ key: `captured:${ids[0]}`, text: n === 1 ? "Etiquetas e anotação guardadas." : `Etiquetas e anotação guardadas nos ${n} itens.` });
    closeCaptured();
  };

  return (
    <section
      ref={box}
      class={`captured${details ? " is-details" : ""}`}
      role="dialog"
      aria-modal="false"
      aria-labelledby="captured-title"
      aria-describedby="captured-line"
      tabIndex={-1}
      onKeyDown={(e) => {
        if (e.key !== "Escape" || e.defaultPrevented) return;
        e.preventDefault();
        closeCaptured();
      }}
    >
      {!details && <span class="captured-timer" aria-hidden="true" />}
      <p class="captured-eyebrow">{n === 1 ? "1 item guardado" : `${n} itens guardados`}</p>
      <h2 id="captured-title" class="captured-title">
        {details ? "Quer etiquetar?" : "Guardado."}
      </h2>
      {!details ? (
        <>
          <p id="captured-line" class="captured-line">
            {offline ? "Fica neste aparelho e sobe para a conta quando a conexão voltar." : "Já está no seu fai.g."}
          </p>
          <div class="captured-actions is-confirm">
            <button type="button" class="btn captured-more" onClick={() => setDetails(true)}>
              <IconPlus />
              Adicionar detalhes
            </button>
            {n === 1 && (
              <button type="button" class="btn" onClick={() => (closeCaptured(), showCard(ids[0]))}>
                Ver
              </button>
            )}
          </div>
        </>
      ) : (
        <>
          <p id="captured-line" class="captured-line">
            Etiquetas e uma anotação ajudam a achar depois. Se preferir, deixe para lá.
          </p>
          <form onSubmit={save}>
            <label for="captured-tags">Etiquetas</label>
            <input
              id="captured-tags"
              value={tags}
              onInput={(e) => setTags(e.currentTarget.value)}
              // Enter nas etiquetas passa para a anotação (enterkeyhint "próximo"); guardar é o botão Salvar.
              onKeyDown={(e) => {
                if (e.key !== "Enter" || e.isComposing) return;
                e.preventDefault();
                document.getElementById("captured-note")?.focus();
              }}
              list="captured-tag-list"
              placeholder="viagem, ideias"
              autocomplete="off"
              enterkeyhint="next"
            />
            <datalist id="captured-tag-list">
              {topTags.value.slice(0, 30).map((t) => (
                <option key={t.id} value={t.name} />
              ))}
            </datalist>
            <label for="captured-note">Anotação</label>
            <textarea id="captured-note" rows={3} value={note} onInput={(e) => setNote(e.currentTarget.value)} placeholder="Algo para lembrar sobre isto…" />
            <div class="captured-actions">
              <button type="button" class="btn" onClick={closeCaptured}>
                Agora não
              </button>
              <button class="btn primary" disabled={busy}>
                Salvar
              </button>
            </div>
          </form>
        </>
      )}
      <button type="button" class="captured-x" aria-label="Fechar" onClick={closeCaptured}>
        <IconClose />
      </button>
    </section>
  );
}
